import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import { X, Search, Download, RefreshCw, ChevronLeft, ChevronRight, Zap, Trash2, RotateCcw, XCircle, CheckCircle2, ArrowRight } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from "recharts";

const SUB_TABS = [
  ["dashboard", "Analytics"],
  ["rules", "Rules"],
  ["events", "Events"],
  ["runs", "Runs"],
  ["audit", "Audit"],
  ["exceptions", "Exceptions"],
  ["approvals", "Approvals"],
];

const RANGES = [["today", "Today"], ["7d", "Last 7d"], ["30d", "Last 30d"], ["custom", "Custom"]];

// ---------------- Shared UI helpers ----------------
function StatusPill({ status }) {
  const map = {
    pending: "bg-[#f5f5f5] text-[#0a0a0a]",
    running: "bg-[#002fa7] text-white",
    completed: "bg-[#008a00] text-white",
    exception: "bg-[#ff3b30] text-white",
    cancelled: "bg-[#4a4a4a] text-white",
    processed: "bg-[#008a00] text-white",
    processing: "bg-[#002fa7] text-white",
    failed: "bg-[#ff3b30] text-white",
    scheduled: "bg-[#f5f5f5] text-[#0a0a0a]",
    published: "bg-[#002fa7] text-white",
    open: "bg-[#ff3b30] text-white",
    assigned: "bg-[#002fa7] text-white",
    escalated: "bg-[#ff3b30] text-white animate-pulse",
    resolved: "bg-[#008a00] text-white",
    approved: "bg-[#008a00] text-white",
    rejected: "bg-[#ff3b30] text-white",
    sent: "bg-[#008a00] text-white",
    skipped: "bg-[#4a4a4a] text-white",
    awaiting_approval: "bg-[#f5b800] text-[#0a0a0a]",
  };
  return <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-widest font-semibold ${map[status] || "bg-[#f5f5f5]"}`}>{status}</span>;
}

function Modal({ title, onClose, children, wide = false }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className={`bg-white border border-[#0a0a0a] w-full ${wide ? "max-w-4xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto`}>
        <div className="flex justify-between items-center px-6 py-4 border-b border-[#0a0a0a] sticky top-0 bg-white z-10">
          <h3 className="font-display font-bold text-xl">{title}</h3>
          <button onClick={onClose} data-testid="modal-close"><X size={18} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function JsonBlock({ data }) {
  return <pre className="bg-[#f5f5f5] p-3 text-xs font-mono overflow-x-auto max-h-64 border border-[#e5e5e5]">{JSON.stringify(data, null, 2)}</pre>;
}

function RangePicker({ value, onChange, custom, setCustom }) {
  return (
    <div className="flex items-center gap-2">
      {RANGES.map(([k, l]) => (
        <button key={k} data-testid={`range-${k}`} onClick={() => onChange(k)}
                className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-widest border ${value === k ? "border-[#002fa7] text-[#002fa7]" : "border-[#e5e5e5] text-[#4a4a4a]"}`}>{l}</button>
      ))}
      {value === "custom" && custom && (
        <div className="flex items-center gap-2 ml-2">
          <input type="datetime-local" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} className="border border-[#0a0a0a] px-2 py-1 text-xs" data-testid="range-from" />
          <span className="text-xs">→</span>
          <input type="datetime-local" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} className="border border-[#0a0a0a] px-2 py-1 text-xs" data-testid="range-to" />
        </div>
      )}
    </div>
  );
}

function Paginator({ total, limit, skip, onChange }) {
  const page = Math.floor(skip / limit) + 1;
  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="flex items-center justify-between py-3 text-xs font-mono">
      <div>Showing {Math.min(skip + 1, total)}–{Math.min(skip + limit, total)} of {total}</div>
      <div className="flex items-center gap-2">
        <button disabled={skip === 0} onClick={() => onChange(Math.max(0, skip - limit))} className="border border-[#0a0a0a] p-1 disabled:opacity-30" data-testid="paginate-prev"><ChevronLeft size={14} /></button>
        <span>Page {page} / {pages}</span>
        <button disabled={page >= pages} onClick={() => onChange(skip + limit)} className="border border-[#0a0a0a] p-1 disabled:opacity-30" data-testid="paginate-next"><ChevronRight size={14} /></button>
      </div>
    </div>
  );
}

function useRange() {
  const [range, setRange] = useState("7d");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const params = useMemo(() => {
    if (range === "custom") return { from: custom.from ? new Date(custom.from).toISOString() : undefined, to: custom.to ? new Date(custom.to).toISOString() : undefined };
    return { range };
  }, [range, custom]);
  return { range, setRange, custom, setCustom, params };
}

// ---------------- Dev Tools ----------------
function DevTools({ env, qc }) {
  const generate = useMutation({
    mutationFn: async () => (await api.post("/engine/dev/generate-demo")).data,
    onSuccess: (d) => { toast.success(`Seeded ${Object.entries(d.seeded).map(([k, v]) => `${v} ${k}`).join(", ")}`); qc.invalidateQueries(); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const reset = useMutation({
    mutationFn: async () => (await api.post("/engine/dev/reset-demo")).data,
    onSuccess: (d) => { toast.success("Demo data reset"); qc.invalidateQueries(); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  if (!env?.demo_tools_enabled) return null;
  return (
    <div className="border border-[#f5b800] bg-[#fffbe6] p-3 mb-6 flex items-center justify-between" data-testid="dev-tools">
      <div className="flex items-center gap-3">
        <Zap size={16} className="text-[#f5b800]" />
        <span className="text-xs font-mono uppercase tracking-widest">Dev Tools · env={env.env}</span>
      </div>
      <div className="flex gap-2">
        <button data-testid="generate-demo-btn" onClick={() => generate.mutate()} disabled={generate.isPending} className="rh-btn-secondary text-xs">{generate.isPending ? "Seeding…" : "Generate Demo Data"}</button>
        <button data-testid="reset-demo-btn" onClick={() => { if (window.confirm("Reset all demo data?")) reset.mutate(); }} disabled={reset.isPending} className="rh-btn-secondary text-xs !border-[#ff3b30] !text-[#ff3b30]">{reset.isPending ? "Resetting…" : "Reset Demo Data"}</button>
      </div>
    </div>
  );
}

// ---------------- Analytics ----------------
function AnalyticsTab() {
  const rng = useRange();
  const { data: summary } = useQuery({
    queryKey: ["ops-summary", rng.params],
    queryFn: async () => (await api.get("/engine/analytics/summary", { params: rng.params })).data,
  });
  const { data: tsEvents } = useQuery({
    queryKey: ["ops-ts-events", rng.params],
    queryFn: async () => (await api.get("/engine/analytics/timeseries", { params: { metric: "events", ...rng.params, buckets: 24 } })).data,
  });
  const { data: tsLat } = useQuery({
    queryKey: ["ops-ts-lat", rng.params],
    queryFn: async () => (await api.get("/engine/analytics/timeseries", { params: { metric: "dispatch_latency", ...rng.params, buckets: 24 } })).data,
  });
  const { data: health } = useQuery({
    queryKey: ["ops-health"],
    queryFn: async () => {
      const r = await fetch(`${BACKEND_URL}/api/health/deep`, { credentials: "include" });
      return r.json();
    },
    refetchInterval: 30000,
  });
  const { data: sysMetrics } = useQuery({
    queryKey: ["ops-metrics"],
    queryFn: async () => (await api.get("/metrics")).data,
    refetchInterval: 30000,
  });

  const fmt = (n) => n == null ? "—" : (typeof n === "number" ? n.toLocaleString() : n);

  const kpis = summary ? [
    ["Events", fmt(summary.events.total), `${summary.events.per_hour}/hr`],
    ["Dispatch p95", summary.dispatch_latency_ms.p95 == null ? "—" : `${summary.dispatch_latency_ms.p95}ms`, `p50 ${summary.dispatch_latency_ms.p50 ?? "—"}ms · p99 ${summary.dispatch_latency_ms.p99 ?? "—"}ms`],
    ["Run success", `${summary.runs.success_rate}%`, `${summary.runs.success}/${summary.runs.total}`],
    ["Run failures", `${summary.runs.failure_rate}%`, `${summary.runs.failed} failed`],
    ["Exceptions", fmt(summary.exceptions.total), `${summary.exceptions.open} open · ${summary.exceptions.avg_age_hours}h avg age`],
    ["Approval backlog", fmt(summary.approvals.backlog), `${summary.approvals.approved} approved · ${summary.approvals.rejected} rejected`],
    ["Retries", fmt(summary.exceptions.retries), "since window start"],
    ["Email delivery", `${summary.notifications.email_success_rate}%`, `${summary.notifications.email_sent}/${summary.notifications.email_total} sent`],
  ] : [];

  const evtChart = (tsEvents?.buckets || []).map(b => ({ t: new Date(b.t).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit" }), value: b.value }));
  const latChart = (tsLat?.buckets || []).map(b => ({ t: new Date(b.t).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit" }), p50: b.p50 || 0, p95: b.p95 || 0 }));

  return (
    <div data-testid="analytics-tab">
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-[#4a4a4a]">Real-time operational metrics</div>
        <RangePicker value={rng.range} onChange={rng.setRange} custom={rng.custom} setCustom={rng.setCustom} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {kpis.map(([label, big, small], i) => (
          <div key={i} className="border border-[#0a0a0a] p-4" data-testid={`kpi-${i}`}>
            <div className="rh-overline">{label}</div>
            <div className="font-display font-black text-2xl rh-num mt-1">{big}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#4a4a4a] mt-1">{small}</div>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="border border-[#0a0a0a] p-4">
          <div className="rh-overline mb-3">Events over time</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={evtChart}>
              <CartesianGrid strokeDasharray="2 2" stroke="#e5e5e5" />
              <XAxis dataKey="t" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip cursor={{ fill: "#f5f5f5" }} />
              <Bar dataKey="value" fill="#002fa7" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-[#0a0a0a] p-4">
          <div className="rh-overline mb-3">Dispatch latency (p50 / p95)</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={latChart}>
              <CartesianGrid strokeDasharray="2 2" stroke="#e5e5e5" />
              <XAxis dataKey="t" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Line type="monotone" dataKey="p50" stroke="#002fa7" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="p95" stroke="#ff3b30" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {summary?.top_failing_rules?.length > 0 && (
        <div className="border border-[#0a0a0a] p-4 mb-6">
          <div className="rh-overline mb-3">Top failing rules</div>
          <div className="grid gap-2">
            {summary.top_failing_rules.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-sm" data-testid={`top-fail-${i}`}>
                <span className="font-mono">{r.rule_id}</span>
                <span className="rh-num font-semibold text-[#ff3b30]">{r.failures} failures</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* System Health */}
      <div className="grid lg:grid-cols-2 gap-4" data-testid="system-health">
        <div className="border border-[#0a0a0a] p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="rh-overline">System Health</div>
            <StatusPill status={health?.status || "unknown"} />
          </div>
          <div className="space-y-2 text-sm">
            {health?.checks && Object.entries(health.checks).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between border-b border-[#e5e5e5] pb-1" data-testid={`health-${k}`}>
                <span className="font-mono uppercase text-xs">{k}</span>
                <div className="flex items-center gap-2">
                  {v.latency_ms != null && <span className="font-mono text-xs text-[#4a4a4a]">{v.latency_ms}ms</span>}
                  <StatusPill status={v.status} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-[#4a4a4a]">
            env: {health?.env || "—"} · updated {health?.ts ? new Date(health.ts).toLocaleTimeString() : "—"}
          </div>
        </div>

        <div className="border border-[#0a0a0a] p-4">
          <div className="rh-overline mb-3">API Metrics</div>
          {sysMetrics ? (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                <div><div className="text-[10px] uppercase tracking-widest text-[#4a4a4a]">Total requests</div><div className="font-display font-black text-xl rh-num">{sysMetrics.total_requests}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-[#4a4a4a]">Error rate</div><div className={`font-display font-black text-xl rh-num ${sysMetrics.error_rate > 0 ? "text-[#ff3b30]" : ""}`}>{sysMetrics.error_rate}%</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-[#4a4a4a]">Uptime</div><div className="font-mono text-xs">{Math.round(sysMetrics.uptime_s / 60)}m</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-[#4a4a4a]">Recent 5xx</div><div className="font-mono text-xs">{sysMetrics.recent_5xx?.length || 0}</div></div>
              </div>
              <div className="border-t border-[#e5e5e5] pt-2">
                <div className="text-[10px] uppercase tracking-widest text-[#4a4a4a] mb-2">Top routes (p95)</div>
                <div className="space-y-1 text-xs font-mono max-h-32 overflow-y-auto">
                  {(sysMetrics.routes || []).slice(0, 6).map((r) => (
                    <div key={r.route} className="flex justify-between">
                      <span className="truncate mr-2">{r.route}</span>
                      <span className="text-[#4a4a4a] whitespace-nowrap">{r.count} · p95 {r.p95_ms ?? "—"}ms</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : <div className="text-xs text-[#4a4a4a]">Loading…</div>}
        </div>
      </div>
    </div>
  );
}

// ---------------- Rules ----------------
function RulesTab() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("");
  const [active, setActive] = useState("");
  const [skip, setSkip] = useState(0);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);

  const params = { q: q || undefined, mode: mode || undefined, active: active === "" ? undefined : active === "true", limit: 20, skip };
  const { data } = useQuery({
    queryKey: ["ops-rules", params],
    queryFn: async () => (await api.get("/engine/rules", { params })).data,
  });

  const toggle = useMutation({
    mutationFn: async ({ rule_id, active }) => (await api.put(`/engine/rules/${rule_id}`, { active })).data,
    onSuccess: () => { toast.success("Rule updated"); qc.invalidateQueries({ queryKey: ["ops-rules"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  return (
    <div data-testid="rules-tab">
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative">
          <Search size={14} className="absolute top-2.5 left-3 text-[#4a4a4a]" />
          <input placeholder="Search…" value={q} onChange={(e) => { setQ(e.target.value); setSkip(0); }} className="border border-[#0a0a0a] pl-9 pr-3 py-2 text-sm w-56" data-testid="rules-search" />
        </div>
        <select value={mode} onChange={(e) => { setMode(e.target.value); setSkip(0); }} className="border border-[#0a0a0a] px-2 py-2 text-sm" data-testid="rules-mode-filter">
          <option value="">All modes</option><option value="automatic">Automatic</option><option value="approval">Approval</option><option value="exception_route">Exception</option>
        </select>
        <select value={active} onChange={(e) => { setActive(e.target.value); setSkip(0); }} className="border border-[#0a0a0a] px-2 py-2 text-sm" data-testid="rules-active-filter">
          <option value="">Any</option><option value="true">Active</option><option value="false">Disabled</option>
        </select>
      </div>
      <div className="border border-[#e5e5e5]">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#e5e5e5] bg-[#f5f5f5] text-[10px] font-mono uppercase tracking-widest text-[#4a4a4a]">
          <div className="col-span-3">Rule</div><div className="col-span-2">Event</div><div className="col-span-1">Mode</div><div className="col-span-1">Active</div><div className="col-span-2">Success</div><div className="col-span-2">Last run</div><div className="col-span-1 text-right">Actions</div>
        </div>
        {(data?.items || []).map((r) => (
          <div key={r.rule_id} className="grid grid-cols-12 gap-2 px-3 py-3 border-b border-[#e5e5e5] items-center text-sm" data-testid={`rule-row-${r.rule_id}`}>
            <div className="col-span-3"><div className="font-semibold text-xs">{r.name}</div><div className="font-mono text-[10px] text-[#4a4a4a]">{r.rule_id}</div></div>
            <div className="col-span-2 text-xs">{r.event}</div>
            <div className="col-span-1"><StatusPill status={r.mode} /></div>
            <div className="col-span-1">
              <label className="inline-flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={!!r.active} onChange={(e) => toggle.mutate({ rule_id: r.rule_id, active: e.target.checked })} data-testid={`rule-toggle-${r.rule_id}`} />
                <span className="text-xs">{r.active ? "On" : "Off"}</span>
              </label>
            </div>
            <div className="col-span-2 text-xs"><span className="font-mono text-[#008a00]">{r.stats?.success_rate}%</span> · {r.stats?.total_runs || 0} runs</div>
            <div className="col-span-2 text-[10px] text-[#4a4a4a] font-mono">{r.stats?.last_execution ? new Date(r.stats.last_execution).toLocaleString() : "—"}</div>
            <div className="col-span-1 flex gap-1 justify-end">
              <button onClick={() => setViewing(r)} className="p-1 hover:bg-[#f5f5f5] text-xs" data-testid={`rule-view-${r.rule_id}`}>View</button>
            </div>
          </div>
        ))}
        {(data?.items || []).length === 0 && <div className="p-8 text-center text-[#4a4a4a] text-sm">No rules match filters.</div>}
      </div>
      <Paginator total={data?.total || 0} limit={20} skip={skip} onChange={setSkip} />
      {viewing && <RuleDetailModal rule={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function RuleDetailModal({ rule, onClose }) {
  const { data: history = [] } = useQuery({
    queryKey: ["rule-hist", rule.rule_id],
    queryFn: async () => (await api.get(`/engine/rules/${rule.rule_id}/history`)).data,
  });
  return (
    <Modal title={rule.name} onClose={onClose} wide>
      <div className="grid md:grid-cols-2 gap-4 mb-4 text-sm">
        <div><div className="rh-overline">Rule ID</div><div className="font-mono">{rule.rule_id}</div></div>
        <div><div className="rh-overline">Event</div><div>{rule.event}</div></div>
        <div><div className="rh-overline">Mode</div><StatusPill status={rule.mode} /></div>
        <div><div className="rh-overline">Stats</div><div className="font-mono text-xs">success {rule.stats?.success_rate}% · fail {rule.stats?.failure_rate}% · {rule.stats?.total_runs} runs</div></div>
      </div>
      <div className="mb-4">
        <div className="rh-overline mb-2">When (conditions)</div>
        <JsonBlock data={rule.when || {}} />
      </div>
      <div className="mb-4">
        <div className="rh-overline mb-2">Then (actions)</div>
        <JsonBlock data={rule.then || []} />
      </div>
      <div>
        <div className="rh-overline mb-2">Version history ({history.length})</div>
        {history.length === 0 ? <div className="text-xs text-[#4a4a4a]">No prior versions.</div> :
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.version_id} className="border border-[#e5e5e5] p-2 text-xs">
                <div className="font-mono">{new Date(h.changed_at).toLocaleString()} · changed {h.changes?.join(", ")}</div>
              </div>
            ))}
          </div>
        }
      </div>
    </Modal>
  );
}

// ---------------- Events ----------------
function EventsTab() {
  const [q, setQ] = useState(""); const [type, setType] = useState(""); const [status, setStatus] = useState("");
  const rng = useRange(); const [skip, setSkip] = useState(0);
  const [viewing, setViewing] = useState(null); const [correlation, setCorrelation] = useState("");

  const params = { q: q || undefined, type: type || undefined, status: status || undefined, ...rng.params, limit: 25, skip };
  const { data } = useQuery({ queryKey: ["ops-events", params], queryFn: async () => (await api.get("/engine/events", { params })).data });

  return (
    <div data-testid="events-tab">
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="relative">
          <Search size={14} className="absolute top-2.5 left-3 text-[#4a4a4a]" />
          <input placeholder="Search entity/type…" value={q} onChange={(e) => { setQ(e.target.value); setSkip(0); }} className="border border-[#0a0a0a] pl-9 pr-3 py-2 text-sm w-56" data-testid="events-search" />
        </div>
        <input placeholder="Type filter…" value={type} onChange={(e) => { setType(e.target.value); setSkip(0); }} className="border border-[#0a0a0a] px-3 py-2 text-sm w-40" data-testid="events-type-filter" />
        <select value={status} onChange={(e) => { setStatus(e.target.value); setSkip(0); }} className="border border-[#0a0a0a] px-2 py-2 text-sm" data-testid="events-status-filter">
          <option value="">Any status</option><option>processed</option><option>failed</option><option>scheduled</option><option>processing</option><option>published</option>
        </select>
        <RangePicker value={rng.range} onChange={rng.setRange} custom={rng.custom} setCustom={rng.setCustom} />
        <input placeholder="Correlation ID…" value={correlation} onChange={(e) => setCorrelation(e.target.value)} className="border border-[#0a0a0a] px-3 py-2 text-sm w-56 ml-auto" data-testid="corr-input" />
        <button onClick={() => correlation && setViewing({ __chain: true, id: correlation })} className="rh-btn-secondary text-xs">Trace</button>
      </div>
      <div className="border border-[#e5e5e5]">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#e5e5e5] bg-[#f5f5f5] text-[10px] font-mono uppercase tracking-widest text-[#4a4a4a]">
          <div className="col-span-2">Timestamp</div><div className="col-span-2">Type</div><div className="col-span-2">Entity</div><div className="col-span-2">Source</div><div className="col-span-1">Latency</div><div className="col-span-1">Status</div><div className="col-span-2 text-right">Actions</div>
        </div>
        {(data?.items || []).map((e) => (
          <div key={e.event_id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#e5e5e5] items-center text-sm" data-testid={`event-row-${e.event_id}`}>
            <div className="col-span-2 text-[10px] font-mono text-[#4a4a4a]">{new Date(e.timestamp).toLocaleString()}</div>
            <div className="col-span-2 text-xs font-semibold">{e.type}</div>
            <div className="col-span-2 text-[10px] font-mono">{e.entity_type}/{e.entity_id.slice(0, 12)}</div>
            <div className="col-span-2 text-xs">{e.source}</div>
            <div className="col-span-1 text-xs rh-num">{e.dispatch_latency_ms ?? "—"}ms</div>
            <div className="col-span-1"><StatusPill status={e.status} /></div>
            <div className="col-span-2 text-right"><button onClick={() => setViewing(e)} className="text-xs underline" data-testid={`event-view-${e.event_id}`}>View</button></div>
          </div>
        ))}
        {(data?.items || []).length === 0 && <div className="p-8 text-center text-[#4a4a4a] text-sm">No events.</div>}
      </div>
      <Paginator total={data?.total || 0} limit={25} skip={skip} onChange={setSkip} />
      {viewing && (viewing.__chain ? <CorrelationChain id={viewing.id} onClose={() => setViewing(null)} /> : <EventDetailModal event={viewing} onClose={() => setViewing(null)} />)}
    </div>
  );
}

function EventDetailModal({ event, onClose }) {
  return (
    <Modal title={`Event · ${event.type}`} onClose={onClose} wide>
      <div className="grid grid-cols-2 gap-3 text-xs mb-4">
        <div><div className="rh-overline">Event ID</div><div className="font-mono">{event.event_id}</div></div>
        <div><div className="rh-overline">Correlation</div><div className="font-mono">{event.correlation_id}</div></div>
        <div><div className="rh-overline">Timestamp</div><div>{new Date(event.timestamp).toLocaleString()}</div></div>
        <div><div className="rh-overline">Source</div><div>{event.source}</div></div>
        <div><div className="rh-overline">Entity</div><div className="font-mono">{event.entity_type}/{event.entity_id}</div></div>
        <div><div className="rh-overline">Status</div><StatusPill status={event.status} /></div>
        <div><div className="rh-overline">Dispatch latency</div><div className="rh-num">{event.dispatch_latency_ms ?? "—"}ms</div></div>
        <div><div className="rh-overline">Scheduled at</div><div className="font-mono">{event.scheduled_at || "—"}</div></div>
      </div>
      <div className="rh-overline mb-2">Payload</div>
      <JsonBlock data={event.payload} />
    </Modal>
  );
}

function CorrelationChain({ id, onClose }) {
  const { data } = useQuery({ queryKey: ["corr", id], queryFn: async () => (await api.get(`/engine/correlation/${id}`)).data });
  return (
    <Modal title={`Trace · ${id}`} onClose={onClose} wide>
      {!data ? <div>Loading…</div> :
        <div className="space-y-6">
          <div><div className="rh-overline mb-2">Events ({data.events.length})</div>
            {data.events.map(e => <div key={e.event_id} className="text-xs font-mono flex gap-2 py-1 border-b border-[#e5e5e5]"><span className="text-[#4a4a4a]">{new Date(e.timestamp).toLocaleTimeString()}</span><StatusPill status={e.status} /><span>{e.type}</span></div>)}
          </div>
          <div><div className="rh-overline mb-2">Runs ({data.runs.length})</div>
            {data.runs.map(r => <div key={r.run_id} className="text-xs font-mono flex gap-2 py-1 border-b border-[#e5e5e5]"><StatusPill status={r.status} /><span>{r.rule_id}</span><ArrowRight size={12} /><span className="text-[#4a4a4a]">{r.steps?.length || 0} steps</span></div>)}
          </div>
          <div><div className="rh-overline mb-2">Audit ({data.audit.length})</div>
            {data.audit.map(a => <div key={a.audit_id} className="text-xs font-mono py-1 border-b border-[#e5e5e5]">{a.action} · <span className="text-[#4a4a4a]">{a.event_type}</span></div>)}
          </div>
        </div>
      }
    </Modal>
  );
}

// ---------------- Runs ----------------
function RunsTab() {
  const qc = useQueryClient();
  const [q, setQ] = useState(""); const [status, setStatus] = useState("");
  const rng = useRange(); const [skip, setSkip] = useState(0);
  const [viewing, setViewing] = useState(null);

  const params = { q: q || undefined, status: status || undefined, ...rng.params, limit: 25, skip };
  const { data } = useQuery({ queryKey: ["ops-runs", params], queryFn: async () => (await api.get("/engine/runs", { params })).data });

  const retry = useMutation({
    mutationFn: async (run_id) => (await api.post(`/engine/runs/${run_id}/retry`)).data,
    onSuccess: () => { toast.success("Retry queued"); qc.invalidateQueries({ queryKey: ["ops-runs"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const cancel = useMutation({
    mutationFn: async (run_id) => (await api.post(`/engine/runs/${run_id}/cancel`)).data,
    onSuccess: () => { toast.success("Cancelled"); qc.invalidateQueries({ queryKey: ["ops-runs"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  return (
    <div data-testid="runs-tab">
      <div className="flex flex-wrap gap-2 mb-4">
        <input placeholder="Search rule / entity / correlation…" value={q} onChange={(e) => { setQ(e.target.value); setSkip(0); }} className="border border-[#0a0a0a] px-3 py-2 text-sm w-72" data-testid="runs-search" />
        <select value={status} onChange={(e) => { setStatus(e.target.value); setSkip(0); }} className="border border-[#0a0a0a] px-2 py-2 text-sm" data-testid="runs-status-filter">
          <option value="">Any status</option><option>running</option><option>awaiting_approval</option><option>completed</option><option>exception</option><option>cancelled</option>
        </select>
        <RangePicker value={rng.range} onChange={rng.setRange} custom={rng.custom} setCustom={rng.setCustom} />
      </div>
      <div className="border border-[#e5e5e5]">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#e5e5e5] bg-[#f5f5f5] text-[10px] font-mono uppercase tracking-widest text-[#4a4a4a]">
          <div className="col-span-2">Started</div><div className="col-span-3">Rule</div><div className="col-span-2">Entity</div><div className="col-span-1">Steps</div><div className="col-span-2">Status</div><div className="col-span-2 text-right">Actions</div>
        </div>
        {(data?.items || []).map((r) => (
          <div key={r.run_id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#e5e5e5] items-center text-sm" data-testid={`run-row-${r.run_id}`}>
            <div className="col-span-2 text-[10px] font-mono text-[#4a4a4a]">{new Date(r.started_at).toLocaleString()}</div>
            <div className="col-span-3 text-xs font-mono">{r.rule_id}</div>
            <div className="col-span-2 text-[10px] font-mono">{r.entity_type}/{r.entity_id.slice(0, 10)}</div>
            <div className="col-span-1 text-xs">{r.steps?.length || 0}</div>
            <div className="col-span-2"><StatusPill status={r.status} /></div>
            <div className="col-span-2 text-right flex gap-2 justify-end">
              <button onClick={() => setViewing(r)} className="text-xs underline" data-testid={`run-view-${r.run_id}`}>View</button>
              <button onClick={() => retry.mutate(r.run_id)} className="p-1 hover:bg-[#f5f5f5]" title="Retry" data-testid={`run-retry-${r.run_id}`}><RotateCcw size={12} /></button>
              <button onClick={() => cancel.mutate(r.run_id)} className="p-1 hover:bg-[#ff3b30] hover:text-white" title="Cancel" data-testid={`run-cancel-${r.run_id}`}><XCircle size={12} /></button>
            </div>
          </div>
        ))}
        {(data?.items || []).length === 0 && <div className="p-8 text-center text-[#4a4a4a] text-sm">No runs.</div>}
      </div>
      <Paginator total={data?.total || 0} limit={25} skip={skip} onChange={setSkip} />
      {viewing && (
        <Modal title={`Run · ${viewing.rule_id}`} onClose={() => setViewing(null)} wide>
          <JsonBlock data={viewing} />
        </Modal>
      )}
    </div>
  );
}

// ---------------- Audit ----------------
function AuditTab() {
  const [q, setQ] = useState(""); const [eventType, setEventType] = useState(""); const [action, setAction] = useState("");
  const rng = useRange(); const [skip, setSkip] = useState(0);

  const params = { q: q || undefined, event_type: eventType || undefined, action: action || undefined, ...rng.params, limit: 25, skip };
  const { data } = useQuery({ queryKey: ["ops-audit", params], queryFn: async () => (await api.get("/engine/audit", { params })).data });

  const download = async (fmt) => {
    try {
      const url = new URL(`${BACKEND_URL}/api/engine/audit/export`);
      Object.entries({ format: fmt, ...params, limit: 10000, skip: undefined }).forEach(([k, v]) => v !== undefined && url.searchParams.set(k, v));
      const resp = await fetch(url.toString(), { credentials: "include" });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `audit_${new Date().toISOString().slice(0, 10)}.${fmt}`;
      a.click();
      toast.success(`Downloaded ${fmt.toUpperCase()}`);
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div data-testid="audit-tab">
      <div className="flex flex-wrap gap-2 mb-4">
        <input placeholder="Search…" value={q} onChange={(e) => { setQ(e.target.value); setSkip(0); }} className="border border-[#0a0a0a] px-3 py-2 text-sm w-56" data-testid="audit-search" />
        <input placeholder="Event type…" value={eventType} onChange={(e) => { setEventType(e.target.value); setSkip(0); }} className="border border-[#0a0a0a] px-3 py-2 text-sm w-40" data-testid="audit-event-filter" />
        <input placeholder="Action…" value={action} onChange={(e) => { setAction(e.target.value); setSkip(0); }} className="border border-[#0a0a0a] px-3 py-2 text-sm w-40" data-testid="audit-action-filter" />
        <RangePicker value={rng.range} onChange={rng.setRange} custom={rng.custom} setCustom={rng.setCustom} />
        <div className="ml-auto flex gap-2">
          <button onClick={() => download("csv")} className="rh-btn-secondary text-xs inline-flex items-center gap-1" data-testid="export-csv-btn"><Download size={12} /> CSV</button>
          <button onClick={() => download("json")} className="rh-btn-secondary text-xs inline-flex items-center gap-1" data-testid="export-json-btn"><Download size={12} /> JSON</button>
        </div>
      </div>
      <div className="border border-[#e5e5e5]">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#e5e5e5] bg-[#f5f5f5] text-[10px] font-mono uppercase tracking-widest text-[#4a4a4a]">
          <div className="col-span-2">Timestamp</div><div className="col-span-2">Event Type</div><div className="col-span-2">Action</div><div className="col-span-3">Entity</div><div className="col-span-2">Rule</div><div className="col-span-1">System</div>
        </div>
        {(data?.items || []).map((a) => (
          <div key={a.audit_id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#e5e5e5] items-center text-xs" data-testid={`audit-row-${a.audit_id}`}>
            <div className="col-span-2 font-mono text-[10px] text-[#4a4a4a]">{new Date(a.timestamp).toLocaleString()}</div>
            <div className="col-span-2 font-semibold">{a.event_type}</div>
            <div className="col-span-2">{a.action}</div>
            <div className="col-span-3 font-mono text-[10px]">{a.entity_type}/{a.entity_id}</div>
            <div className="col-span-2 font-mono text-[10px]">{a.rule_id}</div>
            <div className="col-span-1 text-[10px] text-[#4a4a4a]">{a.system}</div>
          </div>
        ))}
        {(data?.items || []).length === 0 && <div className="p-8 text-center text-[#4a4a4a] text-sm">No audit entries.</div>}
      </div>
      <Paginator total={data?.total || 0} limit={25} skip={skip} onChange={setSkip} />
    </div>
  );
}

// ---------------- Exceptions ----------------
function ExceptionsTab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState(""); const [q, setQ] = useState("");
  const rng = useRange(); const [skip, setSkip] = useState(0);
  const [viewing, setViewing] = useState(null); const [action, setAction] = useState(null);

  const params = { q: q || undefined, status: status || undefined, ...rng.params, limit: 25, skip };
  const { data } = useQuery({ queryKey: ["ops-exc", params], queryFn: async () => (await api.get("/engine/exceptions", { params })).data });

  const resolve = useMutation({
    mutationFn: async ({ id, body }) => (await api.post(`/engine/exceptions/${id}/resolve`, body)).data,
    onSuccess: () => { toast.success("Resolved"); qc.invalidateQueries({ queryKey: ["ops-exc"] }); setAction(null); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const reassign = useMutation({
    mutationFn: async ({ id, body }) => (await api.post(`/engine/exceptions/${id}/reassign`, body)).data,
    onSuccess: () => { toast.success("Reassigned"); qc.invalidateQueries({ queryKey: ["ops-exc"] }); setAction(null); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const escalate = useMutation({
    mutationFn: async ({ id, body }) => (await api.post(`/engine/exceptions/${id}/escalate`, body)).data,
    onSuccess: () => { toast.success("Escalated"); qc.invalidateQueries({ queryKey: ["ops-exc"] }); setAction(null); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const note = useMutation({
    mutationFn: async ({ id, body }) => (await api.post(`/engine/exceptions/${id}/notes`, body)).data,
    onSuccess: () => { toast.success("Note added"); qc.invalidateQueries({ queryKey: ["ops-exc"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  return (
    <div data-testid="exceptions-tab">
      <div className="flex flex-wrap gap-2 mb-4">
        <input placeholder="Search entity / reason…" value={q} onChange={(e) => { setQ(e.target.value); setSkip(0); }} className="border border-[#0a0a0a] px-3 py-2 text-sm w-56" data-testid="exc-search" />
        <select value={status} onChange={(e) => { setStatus(e.target.value); setSkip(0); }} className="border border-[#0a0a0a] px-2 py-2 text-sm" data-testid="exc-status-filter">
          <option value="">All</option><option>open</option><option>assigned</option><option>escalated</option><option>resolved</option>
        </select>
        <RangePicker value={rng.range} onChange={rng.setRange} custom={rng.custom} setCustom={rng.setCustom} />
      </div>
      <div className="border border-[#e5e5e5]">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#e5e5e5] bg-[#f5f5f5] text-[10px] font-mono uppercase tracking-widest text-[#4a4a4a]">
          <div className="col-span-2">Created</div><div className="col-span-2">Type</div><div className="col-span-3">Reason</div><div className="col-span-1">Level</div><div className="col-span-1">Retries</div><div className="col-span-1">Status</div><div className="col-span-2 text-right">Actions</div>
        </div>
        {(data?.items || []).map((x) => (
          <div key={x.exception_id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#e5e5e5] items-center text-sm" data-testid={`exc-row-${x.exception_id}`}>
            <div className="col-span-2 text-[10px] font-mono text-[#4a4a4a]">{new Date(x.created_at).toLocaleString()}</div>
            <div className="col-span-2 text-xs">{x.event_type}</div>
            <div className="col-span-3 text-xs">{x.reason}</div>
            <div className="col-span-1 text-xs">{x.escalation_level || 0}</div>
            <div className="col-span-1 text-xs rh-num">{x.retry_count || 0}</div>
            <div className="col-span-1"><StatusPill status={x.status} /></div>
            <div className="col-span-2 text-right flex gap-1 justify-end">
              <button onClick={() => setViewing(x)} className="text-xs underline" data-testid={`exc-view-${x.exception_id}`}>View</button>
              <button onClick={() => setAction({ type: "resolve", id: x.exception_id })} className="p-1 hover:bg-[#008a00] hover:text-white" title="Resolve" data-testid={`exc-resolve-${x.exception_id}`}><CheckCircle2 size={12} /></button>
              <button onClick={() => setAction({ type: "reassign", id: x.exception_id })} className="p-1 hover:bg-[#f5f5f5]" title="Reassign" data-testid={`exc-reassign-${x.exception_id}`}>↪</button>
              <button onClick={() => setAction({ type: "escalate", id: x.exception_id })} className="p-1 hover:bg-[#ff3b30] hover:text-white" title="Escalate" data-testid={`exc-escalate-${x.exception_id}`}>↑</button>
            </div>
          </div>
        ))}
        {(data?.items || []).length === 0 && <div className="p-8 text-center text-[#4a4a4a] text-sm">No exceptions.</div>}
      </div>
      <Paginator total={data?.total || 0} limit={25} skip={skip} onChange={setSkip} />
      {viewing && <ExceptionDetail exc={viewing} onClose={() => setViewing(null)} onNote={(text) => note.mutate({ id: viewing.exception_id, body: { text } })} />}
      {action && <ExceptionActionModal action={action} onClose={() => setAction(null)}
        onSubmit={(body) => ({ resolve, reassign, escalate })[action.type].mutate({ id: action.id, body })} />}
    </div>
  );
}

function ExceptionDetail({ exc, onClose, onNote }) {
  const [t, setT] = useState("");
  return (
    <Modal title={`Exception · ${exc.exception_id}`} onClose={onClose} wide>
      <div className="grid grid-cols-2 gap-3 text-xs mb-4">
        <div><div className="rh-overline">Reason</div><div>{exc.reason}</div></div>
        <div><div className="rh-overline">Status</div><StatusPill status={exc.status} /></div>
        <div><div className="rh-overline">Rule</div><div className="font-mono">{exc.rule_id}</div></div>
        <div><div className="rh-overline">Assigned</div><div>{exc.assigned_to || "—"}</div></div>
      </div>
      <div className="mb-4">
        <div className="rh-overline mb-2">History ({exc.history?.length || 0})</div>
        <div className="space-y-1 text-xs font-mono max-h-40 overflow-y-auto border border-[#e5e5e5] p-2">
          {(exc.history || []).map((h, i) => <div key={i}>{new Date(h.at).toLocaleString()} · {h.actor} · {h.action}{h.note ? ` · ${h.note}` : ""}</div>)}
        </div>
      </div>
      <div className="mb-4">
        <div className="rh-overline mb-2">Notes ({exc.notes?.length || 0})</div>
        <div className="space-y-1 text-xs max-h-40 overflow-y-auto">
          {(exc.notes || []).map((n, i) => <div key={i} className="border border-[#e5e5e5] p-2">{n.text} <span className="text-[10px] text-[#4a4a4a]">— {n.author} · {new Date(n.at).toLocaleString()}</span></div>)}
        </div>
        <div className="flex gap-2 mt-2">
          <input value={t} onChange={(e) => setT(e.target.value)} placeholder="Add a note…" className="flex-1 border border-[#0a0a0a] px-2 py-1 text-xs" data-testid="exc-note-input" />
          <button onClick={() => { if (t) { onNote(t); setT(""); } }} className="rh-btn-primary text-xs" data-testid="exc-note-submit">Add</button>
        </div>
      </div>
    </Modal>
  );
}

function ExceptionActionModal({ action, onClose, onSubmit }) {
  const [note, setNote] = useState("");
  const [retry, setRetry] = useState(false);
  const [assign, setAssign] = useState("");
  const [level, setLevel] = useState(1);
  const submit = () => {
    if (action.type === "resolve") onSubmit({ retry, note });
    if (action.type === "reassign") onSubmit({ assigned_to: assign, note });
    if (action.type === "escalate") onSubmit({ level: Number(level), note });
  };
  return (
    <Modal title={`${action.type} exception`} onClose={onClose}>
      <div className="space-y-3">
        {action.type === "resolve" && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={retry} onChange={(e) => setRetry(e.target.checked)} data-testid="exc-retry-check" /> Retry the original event</label>}
        {action.type === "reassign" && <label className="block text-sm"><span className="block rh-overline mb-1">Assign to (email / team)</span><input value={assign} onChange={(e) => setAssign(e.target.value)} required className="w-full border border-[#0a0a0a] px-2 py-2 text-sm" data-testid="exc-assign-input" /></label>}
        {action.type === "escalate" && <label className="block text-sm"><span className="block rh-overline mb-1">Escalation level</span><input type="number" min={1} max={5} value={level} onChange={(e) => setLevel(e.target.value)} className="w-full border border-[#0a0a0a] px-2 py-2 text-sm" data-testid="exc-level-input" /></label>}
        <label className="block text-sm"><span className="block rh-overline mb-1">Note (optional)</span><textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full border border-[#0a0a0a] px-2 py-2 text-sm" data-testid="exc-action-note" /></label>
        <div className="flex justify-end gap-2 pt-2 border-t border-[#e5e5e5]">
          <button onClick={onClose} className="rh-btn-secondary text-xs">Cancel</button>
          <button onClick={submit} className="rh-btn-primary text-xs" data-testid="exc-action-submit">Confirm</button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------- Approvals ----------------
function ApprovalsTab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("pending");
  const rng = useRange(); const [skip, setSkip] = useState(0);
  const [action, setAction] = useState(null); const [viewing, setViewing] = useState(null);

  const params = { status: status || undefined, ...rng.params, limit: 25, skip };
  const { data } = useQuery({ queryKey: ["ops-apv", params], queryFn: async () => (await api.get("/engine/approvals", { params })).data });

  const approve = useMutation({
    mutationFn: async ({ id, note }) => (await api.post(`/engine/approvals/${id}/approve`, { note })).data,
    onSuccess: () => { toast.success("Approved"); qc.invalidateQueries({ queryKey: ["ops-apv"] }); setAction(null); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const reject = useMutation({
    mutationFn: async ({ id, reason, note }) => (await api.post(`/engine/approvals/${id}/reject`, { reason, note })).data,
    onSuccess: () => { toast.success("Rejected"); qc.invalidateQueries({ queryKey: ["ops-apv"] }); setAction(null); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const note = useMutation({
    mutationFn: async ({ id, text }) => (await api.post(`/engine/approvals/${id}/notes`, { text })).data,
    onSuccess: () => { toast.success("Note added"); qc.invalidateQueries({ queryKey: ["ops-apv"] }); },
  });

  return (
    <div data-testid="approvals-tab">
      <div className="flex flex-wrap gap-2 mb-4">
        <select value={status} onChange={(e) => { setStatus(e.target.value); setSkip(0); }} className="border border-[#0a0a0a] px-2 py-2 text-sm" data-testid="apv-status-filter">
          <option value="">All</option><option>pending</option><option>approved</option><option>rejected</option>
        </select>
        <RangePicker value={rng.range} onChange={rng.setRange} custom={rng.custom} setCustom={rng.setCustom} />
      </div>
      <div className="border border-[#e5e5e5]">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#e5e5e5] bg-[#f5f5f5] text-[10px] font-mono uppercase tracking-widest text-[#4a4a4a]">
          <div className="col-span-2">Requested</div><div className="col-span-3">Rule</div><div className="col-span-4">Reason</div><div className="col-span-1">Status</div><div className="col-span-2 text-right">Actions</div>
        </div>
        {(data?.items || []).map((a) => (
          <div key={a.approval_id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#e5e5e5] items-center text-sm" data-testid={`apv-row-${a.approval_id}`}>
            <div className="col-span-2 text-[10px] font-mono text-[#4a4a4a]">{new Date(a.requested_at).toLocaleString()}</div>
            <div className="col-span-3 font-mono text-xs">{a.rule_id}</div>
            <div className="col-span-4 text-xs">{a.reason}</div>
            <div className="col-span-1"><StatusPill status={a.status} /></div>
            <div className="col-span-2 text-right flex gap-1 justify-end">
              <button onClick={() => setViewing(a)} className="text-xs underline" data-testid={`apv-view-${a.approval_id}`}>View</button>
              {a.status === "pending" && <>
                <button onClick={() => setAction({ type: "approve", id: a.approval_id })} className="p-1 hover:bg-[#008a00] hover:text-white" title="Approve" data-testid={`apv-approve-${a.approval_id}`}><CheckCircle2 size={12} /></button>
                <button onClick={() => setAction({ type: "reject", id: a.approval_id })} className="p-1 hover:bg-[#ff3b30] hover:text-white" title="Reject" data-testid={`apv-reject-${a.approval_id}`}><XCircle size={12} /></button>
              </>}
            </div>
          </div>
        ))}
        {(data?.items || []).length === 0 && <div className="p-8 text-center text-[#4a4a4a] text-sm">No approvals.</div>}
      </div>
      <Paginator total={data?.total || 0} limit={25} skip={skip} onChange={setSkip} />
      {viewing && <ApprovalDetail apv={viewing} onClose={() => setViewing(null)} onNote={(text) => note.mutate({ id: viewing.approval_id, text })} />}
      {action && <ApprovalActionModal action={action} onClose={() => setAction(null)}
        onSubmit={(body) => (action.type === "approve" ? approve : reject).mutate({ id: action.id, ...body })} />}
    </div>
  );
}

function ApprovalDetail({ apv, onClose, onNote }) {
  const [t, setT] = useState("");
  return (
    <Modal title={`Approval · ${apv.approval_id}`} onClose={onClose} wide>
      <div className="grid grid-cols-2 gap-3 text-xs mb-4">
        <div><div className="rh-overline">Rule</div><div className="font-mono">{apv.rule_id}</div></div>
        <div><div className="rh-overline">Status</div><StatusPill status={apv.status} /></div>
        <div className="col-span-2"><div className="rh-overline">Reason</div><div>{apv.reason}</div></div>
      </div>
      <div className="mb-4"><div className="rh-overline mb-2">Event payload</div><JsonBlock data={apv.event} /></div>
      <div className="mb-4">
        <div className="rh-overline mb-2">History ({apv.history?.length || 0})</div>
        <div className="space-y-1 text-xs font-mono max-h-32 overflow-y-auto border border-[#e5e5e5] p-2">
          {(apv.history || []).map((h, i) => <div key={i}>{new Date(h.at).toLocaleString()} · {h.actor} · {h.action}</div>)}
        </div>
      </div>
      <div>
        <div className="rh-overline mb-2">Notes ({apv.notes?.length || 0})</div>
        <div className="space-y-1 text-xs max-h-32 overflow-y-auto">
          {(apv.notes || []).map((n, i) => <div key={i} className="border border-[#e5e5e5] p-2">{n.text} <span className="text-[10px] text-[#4a4a4a]">— {n.author}</span></div>)}
        </div>
        <div className="flex gap-2 mt-2">
          <input value={t} onChange={(e) => setT(e.target.value)} placeholder="Add a note…" className="flex-1 border border-[#0a0a0a] px-2 py-1 text-xs" data-testid="apv-note-input" />
          <button onClick={() => { if (t) { onNote(t); setT(""); } }} className="rh-btn-primary text-xs" data-testid="apv-note-submit">Add</button>
        </div>
      </div>
    </Modal>
  );
}

function ApprovalActionModal({ action, onClose, onSubmit }) {
  const [note, setNote] = useState(""); const [reason, setReason] = useState("");
  return (
    <Modal title={`${action.type === "approve" ? "Approve" : "Reject"} request`} onClose={onClose}>
      <div className="space-y-3">
        {action.type === "reject" && <label className="block text-sm"><span className="block rh-overline mb-1">Reason</span><input value={reason} onChange={(e) => setReason(e.target.value)} required className="w-full border border-[#0a0a0a] px-2 py-2 text-sm" data-testid="apv-reason-input" /></label>}
        <label className="block text-sm"><span className="block rh-overline mb-1">Note (optional)</span><textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full border border-[#0a0a0a] px-2 py-2 text-sm" data-testid="apv-action-note" /></label>
        <div className="flex justify-end gap-2 pt-2 border-t border-[#e5e5e5]">
          <button onClick={onClose} className="rh-btn-secondary text-xs">Cancel</button>
          <button onClick={() => onSubmit(action.type === "approve" ? { note } : { reason, note })} className="rh-btn-primary text-xs" data-testid="apv-action-submit">Confirm</button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------- Main Container ----------------
export default function AutomationCenter() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("dashboard");
  const { data: env } = useQuery({ queryKey: ["ops-env"], queryFn: async () => (await api.get("/engine/dev/env")).data });

  const TABS = {
    dashboard: <AnalyticsTab />,
    rules: <RulesTab />,
    events: <EventsTab />,
    runs: <RunsTab />,
    audit: <AuditTab />,
    exceptions: <ExceptionsTab />,
    approvals: <ApprovalsTab />,
  };

  return (
    <div data-testid="automation-center">
      <DevTools env={env} qc={qc} />
      <div className="border-b border-[#e5e5e5] flex gap-1 mb-6 overflow-x-auto">
        {SUB_TABS.map(([k, l]) => (
          <button key={k} data-testid={`ops-tab-${k}`} onClick={() => setTab(k)}
                  className={`px-4 py-2 text-xs font-semibold uppercase tracking-widest -mb-px border-b-2 whitespace-nowrap ${tab === k ? "border-[#002fa7] text-[#002fa7]" : "border-transparent text-[#4a4a4a]"}`}>
            {l}
          </button>
        ))}
      </div>
      {TABS[tab]}
    </div>
  );
}
